import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
// The toast wiring needs apply-vs-reject, which stock's DiscountUpdateEvent cannot say:
// it also fires on removal and never on a rejection.
import { dispatchDiscountOutcome } from '@theme/custom.cart-discount-outcome';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLButtonElement} [cartDiscountButton] - Submit, shown once the field has text.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  // cartDiscountButton stays out of requiredRefs: a missing ref throws, and it only drives a
  // cosmetic busy state, so #setApplying optional-chains it instead.
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /**
   * One discount request at a time. Both apply and remove post the full code list read from the
   * pills, and the pills only change once the response morphs in; a second click before then
   * posts a list that still holds the code the first click removed, and it comes back.
   * @type {boolean}
   */
  #pending = false;

  /**
   * The request `#pending` stands for, so checkout can wait it out rather than be dropped.
   * @type {Promise<{code: string | null, applied: boolean}>}
   */
  #inFlight = Promise.resolve({ code: null, applied: false });

  /**
   * Guards `applyBeforeCheckout` against re-entry when it re-clicks the checkout button.
   * @type {boolean}
   */
  #checkoutPending = false;

  connectedCallback() {
    super.connectedCallback();
    this.syncWalletGuard();
  }

  updatedCallback() {
    super.updatedCallback();
    this.syncWalletGuard();
  }

  /**
   * Holds the wallet buttons while the field carries a code the cart has not taken.
   *
   * They are the one route to checkout that applyBeforeCheckout cannot intercept: Shopify
   * renders them in a closed shadow root, and some wallets inside an iframe, so a click on them
   * may never surface in this document. `inert` is what closes both - it takes the wrapper out
   * of pointer and keyboard reach without needing the click at all.
   *
   * Bound to the field's `input` event in snippets/cart-summary.liquid, and re-run on connect and
   * after every section morph, since those rebuild the field and the wrapper together.
   */
  syncWalletGuard = () => {
    // The drawer renders a second field but no wallet buttons; letting it drive the cart page's
    // would strand them inert behind copy the buyer cannot see.
    if (this.closest('cart-drawer-component')) return;

    const blocked = this.#pendingDiscountCode() != null;
    for (const wallets of document.querySelectorAll('.additional-checkout-buttons')) {
      wallets.toggleAttribute('inert', blocked || !!document.querySelector('custom-cart-item-note[note-pending]'));
    }
    this.toggleAttribute('wallet-guarded', blocked);
  };

  /**
   * The code typed into the field but not yet on the cart, or null when there is nothing pending.
   * Compared untrimmed, because that is the value #applyDiscountForm would post.
   *
   * @returns {string | null}
   */
  #pendingDiscountCode() {
    const discountCode = this.querySelector('.cart-discount__form input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return null;

    const pendingCode = discountCode.value;
    if (!pendingCode.trim() || this.#existingDiscounts().includes(pendingCode)) return null;

    return pendingCode;
  }

  /**
   * Applies the code in the field. The field has no always-visible Apply button - the
   * design's empty state has none - so this fires from Enter as often as from a click.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    try {
      await this.#applyDiscountForm(form);
    } finally {
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Applies a code the customer typed but never submitted, before checkout proceeds.
   *
   * Without this, typing a code and going straight to Checkout silently checks out at
   * full price. Invisible whenever there is nothing pending: the event is left alone and
   * the button submits `#cart-form` natively.
   *
   * Only covers the theme's own checkout buttons. The wallet buttons never fire a click this
   * can read, so they are held by syncWalletGuard instead.
   *
   * @param {MouseEvent} event - The click event on the checkout button.
   */
  applyBeforeCheckout = async (event) => {
    if (this.#checkoutPending) return;

    const form = this.querySelector('.cart-discount__form');
    if (!(form instanceof HTMLFormElement)) return;
    const pendingCode = this.#pendingDiscountCode();
    if (!pendingCode) return;

    event.preventDefault();

    // A rejection or morph clears the field, so retain both the requested code and its result.
    const result = this.#pending ? await this.#inFlight : null;
    if (result?.code === pendingCode) {
      if (!result.applied) return;
    } else if (!(await this.#applyDiscountForm(form, pendingCode))) {
      return;
    }

    // Scoped to this summary rather than by id - the cart drawer renders a second
    // `id="checkout"` into the header, and document order would find that one first.
    const checkoutButton = (this.closest('.cart-summary') ?? document).querySelector('#checkout');
    if (!(checkoutButton instanceof HTMLButtonElement)) return;

    // Only needs to hold for the synchronous re-entrant click. Left set, it survives a
    // bfcache restore, and the next pending code would skip the guard entirely.
    this.#checkoutPending = true;
    checkoutButton.click();
    this.#checkoutPending = false;
  };

  /**
   * Posts the field's code to the cart and re-renders the section.
   *
   * @param {HTMLFormElement} form - The discount form.
   * @param {string} [requestedCode] - The code captured before waiting for another mutation.
   * @returns {Promise<boolean>} Whether the code reached the cart. False when it was
   *   rejected, already applied, or the request failed - the caller decides what that means.
   */
  async #applyDiscountForm(form, requestedCode) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return false;
    if (this.#pending) return false;

    const discountCodeValue = requestedCode ?? discountCode.value;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return false;

    this.#pending = true;
    let applied = false;
    /** @type {(value: {code: string | null, applied: boolean}) => void} */
    let settle = () => {};
    this.#inFlight = new Promise((resolve) => (settle = resolve));

    try {
      this.#setApplying(true);

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      this.#markInvalid(false);

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return false;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return false;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      dispatchDiscountOutcome({ outcome: 'applied' });
      // The event above starts a refetch of this same section in cart-items; the response in
      // hand is already that section, so the refetch is dropped before it can land on the morph.
      sectionRenderer.abortRender(this.dataset.sectionId);
      morphSection(this.dataset.sectionId, newHtml);

      applied = true;
      return true;
    } catch (error) {
      // Network failure or malformed response. The message overstates, but applyBeforeCheckout
      // already prevented the checkout click, so silence here would strand the customer.
      console.error(error);
      this.#handleDiscountError('discount_code');
      return false;
    } finally {
      this.#pending = false;
      settle({ code: discountCodeValue, applied });
      this.#setApplying(false);
    }
  }

  /**
   * Reflects the in-flight state. The attribute drives the button's spinner in CSS; the
   * `aria-busy` binding is what a screen reader gets, since CSS cannot set it.
   *
   * @param {boolean} applying - Whether a request is in flight.
   */
  #setApplying(applying) {
    this.toggleAttribute('applying', applying);
    this.refs.cartDiscountButton?.setAttribute('aria-busy', String(applying));
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;
    if (this.#pending) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    this.#pending = true;
    /** @type {(value: {code: string | null, applied: boolean}) => void} */
    let settle = () => {};
    this.#inFlight = new Promise((resolve) => (settle = resolve));
    this.setAttribute('aria-busy', 'true');

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      sectionRenderer.abortRender(this.dataset.sectionId);
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#pending = false;
      settle({ code: null, applied: false });
      this.removeAttribute('aria-busy');
    }
  };

  /**
   * Ties the field to the error while it shows. Only then: aria-describedby reads hidden text too.
   * @param {boolean} invalid
   */
  #markInvalid(invalid) {
    const input = this.querySelector('input[name="discount"]');
    const error = this.refs.cartDiscountError;
    if (!(input instanceof HTMLInputElement)) return;
    if (invalid) {
      input.setAttribute('aria-invalid', 'true');
      if (error.id) input.setAttribute('aria-describedby', error.id);
    } else {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    this.#markInvalid(true);
    // Every rejection path routes through here, so this is the one place that can tell the
    // toast wiring a code was refused.
    dispatchDiscountOutcome({ outcome: 'rejected', type });
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
